// =============================================================================
// rosterFree.js — the pure helpers that let a gated kiosk stop reading the
// downloaded staff roster (Lane BF — closing the pinsForLogin leak).
//
// These are intentionally tiny, browser-free and side-effect-free so they can be
// unit-tested with no DOM and no Supabase (src/lib/rosterFree.test.mjs). Each one
// replaces a spot where foh.jsx / boh-kitchen.jsx used to reach into the
// downloaded `staffPins` / `staffByPin` roster to DECIDE something.
//
// NB: no PIN, no roster, and no network here. Just name/role/initials logic.
// =============================================================================

// The opaque message shown when a clock-OUT PIN is rejected. It MUST be identical
// whether the PIN is unknown OR belongs to a different staffer than the one picked
// — a distinct "no such PIN" reply would leak directory membership (a probing
// staffer could tell "this PIN exists" from "it doesn't"). Kept as one constant so
// the boundary check and the submit backstop can never drift apart.
export const WRONG_PIN_MESSAGE = "Wrong PIN";

// Is a clock-OUT rejected? True when the typed PIN resolved to nobody (unknown),
// OR resolved to someone other than the staffer picked to clock out. The server
// verify returns ONLY the typed PIN's own name (no enumeration), so the
// wrong-person compare stays client-side — this is that compare, pure.
//
// Byte-identical to the two inline checks it replaces:
//   onPinComplete:   !localName || (target && target.name && localName !== target.name)
//   submitClockOut:  !localName || (targetName && localName !== targetName)
export function clockOutRejected(name, targetName) {
  return !name || (!!targetName && name !== targetName);
}

// "Is this roster role an admin?" A staffer's role is a LIST, not one word: the
// live backend's permission gate (`canPerformAction` in the Apps Script) splits
// the directory's role cell on commas and passes if ANY token is allowed, the
// Command Centre's staff editor stores "FOH,Admin" for a dual-role person, and
// the Supabase staff write door validates each comma token against BOH/FOH/Admin.
// This helper used to compare the WHOLE string to "admin", so "FOH,Admin" (or
// "Admin, FOH") was silently not an admin at the till while the backend said it
// was (Lane BT, from the #321 review).
//
// Now: split on comma / semicolon / pipe / whitespace, trim, and match a whole
// token case-insensitively (the 19 Aug sweep's Admin/admin trap stays covered).
// "Administrator" is NOT an admin — the backend matches whole tokens, never
// substrings — and empty/null never is.
export function isAdminRole(role) {
  return String(role || "").toLowerCase().split(/[,;|\s]+/).some((t) => t === "admin");
}

// Auto-derive initials from a display name — the fallback when there is no admin
// override. Byte-identical to the inline logic that used to live in
// foh.jsx `initialsOf`.
export function deriveInitials(name) {
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

// Initials preference order for a Supabase-checklist host, where the initials
// ride ON THE DATA ROW (migration 1100). Prefer the row's initials (the admin
// override, carried per row) and otherwise auto-derive from the name — WITHOUT
// ever touching the downloaded roster. This is what lets a server-PIN-verify
// tablet render checklist initials with no cache read.
export function preferInitials(rowInitials, name) {
  if (rowInitials) return String(rowInitials).toUpperCase();
  return deriveInitials(name);
}
